/**
 * Справочник крупнейших городов России для автозаполнения.
 *
 * Список ориентирован на города > ~50 тыс. жителей плюс региональные центры.
 * Используется в `<CitySelect />` для автодополнения и нормализации значения,
 * полученного через геолокацию (reverse geocoding может вернуть «Moscow» —
 * нормализуем к «Москва»).
 */
const RUSSIAN_CITIES: ReadonlyArray<string> = [
  "Москва",
  "Санкт-Петербург",
  "Новосибирск",
  "Екатеринбург",
  "Казань",
  "Нижний Новгород",
  "Челябинск",
  "Самара",
  "Уфа",
  "Ростов-на-Дону",
  "Краснодар",
  "Омск",
  "Воронеж",
  "Пермь",
  "Волгоград",
  "Саратов",
  "Тюмень",
  "Тольятти",
  "Барнаул",
  "Махачкала",
  "Ижевск",
  "Хабаровск",
  "Ульяновск",
  "Иркутск",
  "Ярославль",
  "Владивосток",
  "Томск",
  "Оренбург",
  "Кемерово",
  "Новокузнецк",
  "Рязань",
  "Набережные Челны",
  "Астрахань",
  "Пенза",
  "Липецк",
  "Тула",
  "Киров",
  "Чебоксары",
  "Калининград",
  "Балашиха",
  "Курск",
  "Севастополь",
  "Сочи",
  "Ставрополь",
  "Улан-Удэ",
  "Тверь",
  "Магнитогорск",
  "Иваново",
  "Брянск",
  "Симферополь",
  "Белгород",
  "Архангельск",
  "Сургут",
  "Владимир",
  "Чита",
  "Калуга",
  "Якутск",
  "Смоленск",
  "Саранск",
  "Череповец",
  "Волжский",
  "Курган",
  "Орёл",
  "Подольск",
  "Грозный",
  "Вологда",
  "Мурманск",
  "Тамбов",
  "Кострома",
  "Нижневартовск",
  "Новороссийск",
  "Йошкар-Ола",
  "Таганрог",
  "Комсомольск-на-Амуре",
  "Сыктывкар",
  "Нижнекамск",
  "Шахты",
  "Дзержинск",
  "Братск",
  "Орск",
  "Ангарск",
  "Энгельс",
  "Старый Оскол",
  "Великий Новгород",
  "Псков",
  "Бийск",
  "Прокопьевск",
  "Балаково",
  "Рыбинск",
  "Северодвинск",
  "Армавир",
  "Петрозаводск",
  "Уссурийск",
  "Южно-Сахалинск",
  "Норильск",
  "Сызрань",
  "Каменск-Уральский",
  "Златоуст",
  "Электросталь",
  "Альметьевск",
  "Салават",
  "Миасс",
  "Находка",
  "Копейск",
  "Пятигорск",
  "Рубцовск",
  "Березники",
  "Майкоп",
  "Хасавюрт",
  "Коломна",
  "Одинцово",
  "Кисловодск",
  "Нальчик",
  "Серпухов",
  "Новочеркасск",
  "Нефтеюганск",
  "Дербент",
  "Черкесск",
  "Орехово-Зуево",
  "Ноябрьск",
  "Камышин",
  "Невинномысск",
  "Артём",
  "Магадан",
  "Соликамск",
  "Раменское",
  "Долгопрудный",
  "Реутов",
  "Жуковский",
  "Пушкино",
  "Сергиев Посад",
  "Анапа",
  "Геленджик",
  "Тобольск",
  "Тюмень",
  "Кызыл",
  "Глазов",
  "Кызылорда",
];

const NORMALIZED_INDEX = (() => {
  const m = new Map<string, string>();
  for (const c of RUSSIAN_CITIES) {
    m.set(c.toLowerCase().replace(/ё/g, "е"), c);
  }
  return m;
})();

/** Нормализует «moscow», «Москва́», «москва» к каноничному «Москва». */
export function normalizeCityName(input: string): string {
  const raw = (input || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase().replace(/ё/g, "е");
  const direct = NORMALIZED_INDEX.get(lower);
  if (direct) return direct;
  // Часто Nominatim возвращает английские названия — простая таблица соответствий.
  const aliases: Record<string, string> = {
    moscow: "Москва",
    "saint petersburg": "Санкт-Петербург",
    "st. petersburg": "Санкт-Петербург",
    "st petersburg": "Санкт-Петербург",
    "saint-petersburg": "Санкт-Петербург",
    "nizhny novgorod": "Нижний Новгород",
    novosibirsk: "Новосибирск",
    yekaterinburg: "Екатеринбург",
    ekaterinburg: "Екатеринбург",
    kazan: "Казань",
    chelyabinsk: "Челябинск",
    samara: "Самара",
    ufa: "Уфа",
    "rostov-on-don": "Ростов-на-Дону",
    krasnodar: "Краснодар",
    omsk: "Омск",
    voronezh: "Воронеж",
    perm: "Пермь",
    volgograd: "Волгоград",
    saratov: "Саратов",
    tyumen: "Тюмень",
    sochi: "Сочи",
    vladivostok: "Владивосток",
    kaliningrad: "Калининград",
    yaroslavl: "Ярославль",
  };
  return aliases[lower] || raw;
}

export function searchCities(query: string, limit = 10): string[] {
  const q = (query || "").trim().toLowerCase().replace(/ё/g, "е");
  if (!q) return [];
  const out: string[] = [];
  // Префиксное совпадение приоритетнее, чем содержит.
  const prefix: string[] = [];
  const contains: string[] = [];
  for (const city of RUSSIAN_CITIES) {
    const lc = city.toLowerCase().replace(/ё/g, "е");
    if (lc.startsWith(q)) prefix.push(city);
    else if (lc.includes(q)) contains.push(city);
    if (prefix.length >= limit) break;
  }
  for (const c of prefix) {
    if (!out.includes(c)) out.push(c);
    if (out.length >= limit) return out;
  }
  for (const c of contains) {
    if (!out.includes(c)) out.push(c);
    if (out.length >= limit) return out;
  }
  return out;
}

/**
 * Валидация названия города (свободный ввод разрешён — малые НП не все в справочнике,
 * но символы ограничиваем: буквы, пробелы, дефисы, точки и скобки).
 * Пустая строка валидна — поля города могут быть необязательными.
 */
export function getCityNameError(value: string): string | null {
  const v = (value || "").trim();
  if (!v) return null;
  if (v.length < 2) return "Название города слишком короткое";
  if (!/^[а-яёА-ЯЁa-zA-Z][а-яёА-ЯЁa-zA-Z\s\-.()]*$/.test(v)) {
    return "Название города может содержать только буквы, пробелы и дефисы";
  }
  return null;
}


// --- Город «только из списка» при регистрации (#1335) ------------------------

const PICKED_STORAGE_KEY = "rla.pickedCities";

/** Города, выбранные пользователем из подсказок Dadata / геолокации в этой сессии. */
const pickedCities = new Set<string>();
let pickedLoaded = false;

function cityKey(value: string): string {
  return (value || "").trim().toLowerCase().replace(/ё/g, "е");
}

function loadPicked(): void {
  if (pickedLoaded) return;
  pickedLoaded = true;
  try {
    const raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(PICKED_STORAGE_KEY) : null;
    const list: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(list)) for (const c of list) if (typeof c === "string") pickedCities.add(cityKey(c));
  } catch {
    // sessionStorage недоступен или испорчен — работаем только со справочником.
  }
}

/**
 * Запоминает город, выбранный из подсказок (Dadata, справочник, геолокация).
 * Хранится в sessionStorage, чтобы восстановленный из черновика визард не
 * заставлял выбирать уже выбранный город заново.
 */
export function rememberPickedCity(value: string): void {
  const key = cityKey(value);
  if (!key) return;
  loadPicked();
  pickedCities.add(key);
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(PICKED_STORAGE_KEY, JSON.stringify([...pickedCities].slice(-50)));
    }
  } catch {
    // квота/приватный режим — не критично
  }
}

export const CITY_NOT_FROM_LIST_ERROR = "Выберите город из списка подсказок";

/**
 * Строгая проверка для регистрации: значение принимается, только если оно из
 * справочника или было выбрано из подсказок (то же, что видит пользователь в
 * выпадающем списке). Произвольный текст — ошибка. Пустая строка валидна
 * (необязательность поля решает вызывающий). Для редактирования профиля
 * существующих пользователей не используется — там действует getCityNameError.
 */
export function getCityChoiceError(value: string): string | null {
  const v = (value || "").trim();
  if (!v) return null;
  const formatError = getCityNameError(v);
  if (formatError) return formatError;
  const key = cityKey(v);
  if (NORMALIZED_INDEX.has(key)) return null;
  loadPicked();
  return pickedCities.has(key) ? null : CITY_NOT_FROM_LIST_ERROR;
}

/** Только для тестов: сбросить запомненный выбор. */
export function resetPickedCitiesForTests(): void {
  pickedCities.clear();
  pickedLoaded = true;
}
